/**
 * @file api-client.ts
 * @description Тонкая обёртка над fetch для @chaiberi/admin (phase04 шаг 3).
 *
 * Контракт:
 * - Все эндпоинты живут под `${API_URL}/...` (по умолчанию `/api/v1`,
 *   проксируется vite dev-сервером на http://localhost:3001/api).
 * - `credentials: 'include'` — браузер шлёт cookie `chaiberi.sid` с каждым
 *   запросом и принимает Set-Cookie из ответа.
 * - API возвращает либо `{ data }`, либо `{ error: { code, message, details? } }`
 *   (см. apps/api/src/common/api-exception.filter.ts). `apiFetch` распарсит
 *   ответ и:
 *   - на 2xx вернёт `data` напрямую;
 *   - на 401 — дополнительно диспатчит `auth:unauthenticated` (CustomEvent),
 *     чтобы AuthProvider сбросил состояние без явных вызовов из каждого
 *     компонента;
 *   - на ошибку бросит `ApiError` с полями code/message/status.
 *
 * Сетевые ошибки (отсутствие соединения, CORS-преcrash) маппятся в
 * ApiError(code='network_error').
 *
 * phase19.31 (SC21–SC23, SC42): каждый запрос несёт кабинет ЭТОЙ вкладки —
 * заголовок `X-Commora-Context` (`lib/tab-context.ts`; `/auth/*`, кроме
 * `/auth/me`, — без него). Отказы «кабинет вкладки чужой» (403
 * `rbac_tab_context_forbidden`) и «идёт вход под сотрудником» (409
 * `impersonation_active`) получают понятный текст и событие
 * `TAB_CONTEXT_CONFLICT_EVENT` — окно с действием рисует
 * `TabContextConflictDialog`, страницам ничего особого делать не нужно.
 */

import { tabContextHeaders } from "./tab-context";

/** База API кабинета (по умолчанию `/api/v1`; абсолютный адрес — API на другом origin). */
export const API_URL: string = import.meta.env.VITE_API_URL ?? "/api/v1";

export const AUTH_UNAUTHENTICATED_EVENT = "auth:unauthenticated";
/**
 * 403 с code='rbac_no_active_context' — сессия есть, но активный контекст
 * не выбран (или истёк). AuthProvider/Router слушают это и редиректят на
 * /select-context.
 */
export const RBAC_NO_ACTIVE_CONTEXT_EVENT = "auth:no-active-context";

/** Отказ из-за кабинета вкладки (SC22, SC42): `detail` — `{ code, path }`. */
export const TAB_CONTEXT_CONFLICT_EVENT = "auth:tab-context-conflict";

/**
 * Коды отказов «не тот кабинет у вкладки» → текст для человека. Реестр, а не
 * условие: новый код отказа — новая строка.
 */
export const TAB_CONTEXT_CONFLICT_TEXT: Readonly<Record<string, string>> = {
  rbac_tab_context_forbidden:
    "Эта вкладка открыта для кабинета, к которому у вас сейчас нет доступа. Откройте свой кабинет.",
  impersonation_active:
    "Вход под сотрудником ещё не завершён — завершите его или продолжайте в его кабинете.",
};

/** Код отказа из-за кабинета вкладки? */
export function isTabContextConflict(code: string): boolean {
  return Object.hasOwn(TAB_CONTEXT_CONFLICT_TEXT, code);
}

export interface ApiErrorPayload {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown;

  constructor(payload: ApiErrorPayload, status: number) {
    super(payload.message);
    this.name = "ApiError";
    this.code = payload.code;
    this.status = status;
    this.details = payload.details;
  }
}

export interface ApiFetchOptions extends Omit<RequestInit, "body"> {
  /** JSON-тело — сериализуется в `application/json`. */
  json?: unknown;
  /**
   * Multipart-тело (загрузка файлов, phase19.16 Р1-7). `Content-Type` с
   * границей ставит браузер — заголовок не задаём. С `json` не сочетается.
   */
  form?: FormData;
}

/**
 * Универсальный вызов API.
 *
 * @example
 *   const me = await apiFetch<MeResponse>('/auth/me');
 *   await apiFetch('/auth/login', { method: 'POST', json: { email, password } });
 */
export async function apiFetch<T = unknown>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const envelope = await requestEnvelope<T, unknown>(path, options);
  return envelope.data as T;
}

/**
 * То же, что `apiFetch`, но сохраняет `meta` рядом с `data`.
 *
 * Нужен эндпоинтам с серверной пагинацией, где API отдаёт
 * `{ data: [...], meta: { total, page, pageSize } }` — `apiFetch` вернул бы
 * только `data` и потерял бы `total`.
 */
export async function apiFetchWithMeta<T = unknown, M = unknown>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<{ data: T; meta: M }> {
  const envelope = await requestEnvelope<T, M>(path, options);
  return { data: envelope.data as T, meta: envelope.meta as M };
}

/**
 * Полный конверт ответа — для эндпоинтов, которые кладут данные РЯДОМ с
 * `data` (phase19.24 этап 1, шаг 4: размещение отдаёт
 * `{ data, skipped }`). `apiFetch` вернул бы только `data` и потерял бы
 * список пропущенных партий, `apiFetchWithMeta` знает лишь про `meta`.
 *
 * Ошибки разбираются так же, как в `apiFetch`: на не-2xx летит `ApiError`.
 */
export async function apiFetchEnvelope<E extends object>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<E> {
  return (await requestEnvelope<unknown, unknown>(path, options)) as E;
}

/** Файл, скачанный с API: содержимое + имя из Content-Disposition. */
export interface ApiFileResponse {
  blob: Blob;
  filename: string;
}

/**
 * Запрос за файлом (PDF печатных форм и т. п.).
 *
 * Отличается от `apiFetch` тем, что тело ответа — не JSON. Ошибки при этом
 * приходят обычным конвертом `{ error }`, поэтому разбираем их так же и
 * бросаем `ApiError` — вызывающий код показывает toast, а не падает.
 */
export async function apiDownload(
  path: string,
  options: ApiFetchOptions = {},
  fallbackFilename = "file",
): Promise<ApiFileResponse> {
  const { json, headers, ...rest } = options;
  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      ...rest,
      credentials: "include",
      headers: {
        ...tabContextHeaders(path),
        ...(json !== undefined ? { "Content-Type": "application/json" } : {}),
        ...headers,
      },
      body: json !== undefined ? JSON.stringify(json) : undefined,
    });
  } catch (cause) {
    throw new ApiError(
      {
        code: "network_error",
        message: cause instanceof Error ? cause.message : "Сетевая ошибка",
      },
      0,
    );
  }

  if (!response.ok) {
    const text = await response.text();
    let payload: { error?: ApiErrorPayload } = {};
    try {
      payload = text ? (JSON.parse(text) as { error?: ApiErrorPayload }) : {};
    } catch {
      // Сервер вернул не-JSON (например, HTML прокси) — оставим общий текст.
    }
    throw toApiError(path, response.status, payload.error);
  }

  return {
    blob: await response.blob(),
    filename: filenameFromDisposition(response.headers.get("Content-Disposition"), fallbackFilename),
  };
}

/** `attachment; filename="x.pdf"` / `filename*=UTF-8''x.pdf` → имя файла. */
function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const encoded = /filename\*=UTF-8''([^;]+)/iu.exec(header);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1].trim());
    } catch {
      // Битая кодировка — пробуем обычный filename ниже.
    }
  }
  const plain = /filename="?([^";]+)"?/iu.exec(header);
  return plain?.[1]?.trim() || fallback;
}

async function requestEnvelope<T, M>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<{ data?: T; meta?: M }> {
  const { json, form, headers, ...rest } = options;
  const init: RequestInit = {
    ...rest,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...tabContextHeaders(path),
      ...(json !== undefined ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    body: json !== undefined ? JSON.stringify(json) : form,
  };

  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, init);
  } catch (cause) {
    throw new ApiError(
      {
        code: "network_error",
        message: cause instanceof Error ? cause.message : "Сетевая ошибка",
      },
      0,
    );
  }

  // 204 No Content
  if (response.status === 204) {
    return {};
  }

  const text = await response.text();
  // допускаем пустой ответ от API (на всякий случай) — оборачиваем в null
  const payload: { data?: T; meta?: M; error?: ApiErrorPayload } = text
    ? (JSON.parse(text) as { data?: T; meta?: M; error?: ApiErrorPayload })
    : {};

  if (response.ok) {
    return payload;
  }

  throw toApiError(path, response.status, payload.error);
}

/**
 * Разбор ошибочного ответа — общий для JSON-запросов и скачивания файлов:
 * события для провайдера авторизации и окна «не тот кабинет», понятный
 * текст вместо серверного у отказов из-за кабинета вкладки. Экспортирован
 * для загрузки с процентом (`upload-with-progress.ts`, XHR) — те же события.
 */
export function toApiError(path: string, status: number, payload: ApiErrorPayload | undefined): ApiError {
  const error: ApiErrorPayload = payload ?? { code: "api_error", message: `HTTP ${status}` };
  if (status === 401) {
    window.dispatchEvent(new CustomEvent(AUTH_UNAUTHENTICATED_EVENT));
  }
  if (status === 403 && error.code === "rbac_no_active_context") {
    window.dispatchEvent(new CustomEvent(RBAC_NO_ACTIVE_CONTEXT_EVENT));
  }
  const conflictText = TAB_CONTEXT_CONFLICT_TEXT[error.code];
  if (conflictText) {
    window.dispatchEvent(new CustomEvent(TAB_CONTEXT_CONFLICT_EVENT, { detail: { code: error.code, path } }));
    return new ApiError({ ...error, message: conflictText }, status);
  }
  return new ApiError(error, status);
}
